export type YieldArithmeticIssue = {
  step: "steel_quantity" | "steel_cost" | "cost_per_volume" | "volume" | "yield";
  label: string;
  expected: number;
  reported: number;
  unit: string;
};
export type YieldArithmeticAudit = {
  status: "unrecognized" | "consistent" | "inconsistent";
  issues: YieldArithmeticIssue[];
};
// Audit only explicit Portuguese calculation chains. Never repairs evidence or approves yields.
export function auditHistoricalYieldEvidence(evidence: string | null, quantity?: number): YieldArithmeticAudit {
  const token = "(\\d{1,3}(?:\\.\\d{3})+(?:,\\d+)?|\\d+(?:,\\d+)?)";
  const pattern = new RegExp("^(?:varão\\s+)?aço\\s*-\\s*" + token +
    "\\s*m(?:3|³)\\s*[x×]\\s*" + token + "\\s*kg\\s*=\\s*" + token +
    "\\s*kg\\s*[x×]\\s*" + token + "\\s*€\\s*=\\s*" + token +
    "\\s*€\\s*[:÷]\\s*" + token + "\\s*m(?:3|³)\\s*=\\s*" + token + "\\s*€\\s*$", "i");
  const match = evidence?.trim().match(pattern);
  if (!match) return { status: "unrecognized", issues: [] };
  const texts = match.slice(1);
  const values = texts.map(text => Number(text.replaceAll(".", "").replace(",", ".")));
  if (values.some(value => !Number.isFinite(value) || value < 0)
    || values[0] <= 0 || values[5] <= 0) return { status: "unrecognized", issues: [] };
  const [volume, yieldValue, steel, price, cost, divisor, unitCost] = values;
  const issues: YieldArithmeticIssue[] = [];
  function compare(step: YieldArithmeticIssue["step"], label: string, expected: number, reported: number, text: string, unit: string) {
    // A whole-number source result can legitimately be rounded to the nearest whole number.
    const digits = text.split(",")[1]?.length ?? 0;
    const tolerance = 0.5 * 10 ** -digits;
    if (Math.abs(expected - reported) > tolerance + 1e-8) issues.push({ step, label, expected, reported, unit });
  }
  compare("steel_quantity", "Volume × consumo de aço", volume * yieldValue, steel, texts[2], "kg");
  compare("steel_cost", "Quantidade de aço × preço", steel * price, cost, texts[4], "€");
  compare("cost_per_volume", "Custo ÷ volume", cost / divisor, unitCost, texts[6], "€/m³");
  // Repeated volume is an identity, not a rounded calculation result.
  if (volume !== divisor) issues.push({ step: "volume", label: "Volume usado no cálculo e na divisão", expected: volume, reported: divisor, unit: "m³" });
  if (quantity !== undefined && Number.isFinite(quantity) && quantity !== yieldValue)
    issues.push({ step: "yield", label: "Consumo na nota e rendimento extraído", expected: yieldValue, reported: quantity, unit: "kg/m³" });
  return { status: issues.length ? "inconsistent" : "consistent", issues };
}
